import { action, makeObservable, observable } from 'mobx';
import samplesModel from '../SamplesModel';
import audioPlayerModel from '../../AudioPlayer/AudioPlayerModel';
import { apiSwagger } from '../../../shared/api';
import SamplesFinder from './SamplesFinder';
import samplesDiagnosticPopupModel from '../SamplesDiagnosticPopup/SamplesDiagnosticPopupModel';
import type { Category, Sample } from '@/sharedTypes/Api';
import { getStores } from '@/stores/stores';
import { isAdminPage, isDebugPage, rightLanguage } from '@/sharedTypes/helpers';
import { triggerEvent } from '@/sharedTypes/pureHelpers';
import { safeWindow, registerGlobalInstance } from '@/sharedTypes/browserUtils';
import { ManagerEntity } from '@/shared/globalFunctions';
import MapperEntity from '@/entities/Mapper/Mapper';
import toast from '@/components/ui/custom-toast';
import { t } from '@/shared/i18n';
import { FavoriteItem } from '@/types/favorites';
import etaLoader from '@/lib/EtaLoader';

export class SamplesSelectorModel {
  get category(): Category | null {
    if (this.selectedSample) {
      return rightLanguage(this.selectedSample.category);
    } else {
      return null;
    }
  }

  get title(): string | null {
    if (this.selectedSample) {
      if (this.selectedSample.metadata.title) {
        return rightLanguage(this.selectedSample.metadata.title);
      }
    }
    return null;
  }

  get categories(): Category[] {
    const categories: Category[] = [];
    for (const sample of this.samples) {
      const category: Category = sample.category;
      if (category && !categories.find(cat => cat._id === category._id)) {
        categories.push(category);
      }
    }
    return categories;
  }

  samples: Sample[] = [];

  samplesFinder: SamplesFinder | null = null;

  // there will be exact version like 2.1.3, after fetch.
  private coreVersion: string | null = null;

  // Control samples menu open/close state
  samplesMenuOpen: boolean | null = null; // null = no change, true = open, false = close

  applySearch(query: string, areFavoritesShown?: boolean, favoriteItems?: FavoriteItem[]) {
    if (!this.samplesFinder) {}
    let samplesToSearch = this.samples;
    
    if (areFavoritesShown && favoriteItems) {
      const favoriteIdsArray = favoriteItems.map(f => f.id.toString());
      samplesToSearch = this.samples.filter(sample =>
        favoriteIdsArray.includes(sample.identifier.toString())
      );
    }
    this.samplesFinder = new SamplesFinder(samplesToSearch);
    return this.samplesFinder.search(query);
  }

  selectedSample: Sample | null = null;

  constructor() {
    makeObservable(this, {
      samples: observable,
      selectedSample: observable,
      samplesMenuOpen: observable,
      selectSample: action,
      refreshSamples: action,
      setSamplesMenuOpen: action,
    });
  }

  setSamplesMenuOpen(open: boolean | null) {
    this.samplesMenuOpen = open;
  }
  async refreshSamples() {
    let progress = 0;
    let timer: any;
    if (typeof window !== 'undefined' && etaLoader) {
      etaLoader.setStage('preparingMusicEngine', progress);
      timer = setInterval(() => {
        progress = Math.min(progress + 0.005, 0.95);
        etaLoader.setStage('preparingMusicEngine', progress);
      }, 50);
      etaLoader.setContext('refreshSamples');
      etaLoader.setStage('refreshSamples_start', 0);
    }
    const showAll = isAdminPage() || isDebugPage();
    const response = await apiSwagger.samples.samplesList({ showAll });
    if(typeof window !== 'undefined' && etaLoader) {
      etaLoader.setStage('refreshSamples_start', 0.5);
    }
    const data: any = await response.json();
    if (typeof window !== 'undefined' && etaLoader) {
      clearInterval(timer);
      etaLoader.setStage('preparingMusicEngine', 1);
      etaLoader.setStage('refreshSamples_start', 0.7);
      etaLoader.setStage('almostReady', 0);
    }

    this.samples = [];
    etaLoader.setStage('refreshSamples_start', 0.9);

    if (data && !data.error && data.allSamples && data.allSamples.length > 0) {
        etaLoader.setStage('refreshSamples_start', 1);
        etaLoader.setStage('refreshSamples_done', 0);

        // Backend handles filtering
        this.samples = data.allSamples;

        etaLoader.setStage('refreshSamples_done', 0.4);
        triggerEvent('samplesRefreshed');
        triggerEvent('samplesRefreshed_playwright');
        etaLoader.setStage('refreshSamples_done', 0.6);
        etaLoader.setStage('almostReady', 0.5);
        const interval = setInterval(() => {
          triggerEvent('samplesRefreshed_playwright');
        }, 200);
        setTimeout(() => {
          clearInterval(interval);
        }, 560000);

        etaLoader.setStage('refreshSamples_done', 0.8);
    } else {
      toast({
        title: t('error'),
        description: t('samples.refresh_failed'),
        type: 'error',
      });
      console.error(`Failed to get samples from response ${data.error}`);
    }

    if(typeof window !== 'undefined' && etaLoader) {
      etaLoader.setStage('refreshSamples_done', 1);
      etaLoader.setStage('almostReady', 1);
    }
  }

  async triggerSampleSelection(sampleIdentifier: string) {
    const isAdmin = isAdminPage();
    const isDebug = isDebugPage();
    
    if (!isAdmin && !isDebug) {
      const url = safeWindow.location.getHref();
      const pathname = safeWindow.location.getPathname();
      const isRussianRoute = pathname.startsWith('/ru/');

      if (url.includes('play')) {
        const playUrl = isRussianRoute ? `/ru/play?m=${sampleIdentifier}` : `/play?m=${sampleIdentifier}`;
        safeWindow.location.pushState(null, '', playUrl);
      } else {
        const practiceUrl = isRussianRoute ? `/ru/practice?m=${sampleIdentifier}` : `/practice?m=${sampleIdentifier}`;
        safeWindow.location.pushState(null, '', practiceUrl);
      }
    } else {
      samplesSelectorModel.selectSample(sampleIdentifier);
    }
  }

  async selectSample(sampleIdentifier: string) {
    const totalStart = performance.now();
    console.log('[PERF] ========== selectSample START ==========');

    // Start a new loading session - this cancels any previous parallel executions
    let sessionId: string | null = null;
    if (typeof window !== 'undefined' && etaLoader) {
      sessionId = etaLoader.startSession();
    }


    try {
      if (sampleIdentifier) {
        // Only start loadXML context if global context is finished
        if (typeof window !== 'undefined' && etaLoader && sessionId) {
          // Check if global context is still running
          if (etaLoader.context !== 'global' && etaLoader.globalContextFinished) {
           etaLoader.setContext('loadXML', sessionId);
           etaLoader.setStage('loadXML_start', 0, undefined, sessionId);
          }
        }
      // Fetch initial version of core from server if we don't have one yet
      if (this.coreVersion === null) {
        try {
          const response = await fetch('/api/coreUpdated');
          if (response.ok) {
            const serverConfig = await response.json();
            this.coreVersion = serverConfig.version;
          }
        } catch (error) {
          console.warn('[SamplesSelector] Failed to fetch initial core version:', error);
        }
      }

      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('sample', sampleIdentifier);
      }
      // safeWindow.spinner.show('Loading musicsheet...');
      if (typeof window !== 'undefined' && etaLoader && sessionId) {
        etaLoader.setStage('loadXML_start', 0.3, undefined, sessionId);
      }
      samplesDiagnosticPopupModel.startMessageListening();
      const apiStart = performance.now();
      const response = await apiSwagger.samples.samplesDetail(sampleIdentifier);
      if (response.ok) {
        const data = await response.json();
        console.log(`[PERF] API samplesDetail: ${(performance.now() - apiStart).toFixed(0)}ms`);

        // Check if server-rendered SVGs are available
        const hasServerRenderedSvgs = data.svgUrls?.length > 0 && data.noteCoordinatesUrl && data.cacheUrl;

        // Fetch MusicXML from CDN URL
        // Needed for: 1) Legacy flow (no SSR), 2) Admin pages (XmlEditor/Mapper needs it for finger editing)
        let encryptedMusicxml = '';
        let cache: string | undefined = undefined;

        if (data.musicxmlUrl && (!hasServerRenderedSvgs || isAdminPage())) {
          const musicxmlResponse = await fetch(data.musicxmlUrl);
          const musicxmlEncryptedText = await musicxmlResponse.text();
          encryptedMusicxml = musicxmlEncryptedText;
        }

        // Fetch cache from CDN URL (only for legacy flow - server-rendered uses cacheUrl directly)
        if (!hasServerRenderedSvgs && data.cacheUrl) {
          const cacheResponse = await fetch(data.cacheUrl);
          const cacheText = await cacheResponse.text();
          if(data.encrypted) {
            cache = ManagerEntity.decryptText(cacheText);
          } else {
            cache = cacheText;
          }
        } else if (!hasServerRenderedSvgs) {
          console.warn('Предупреждение: Кеш отсутствует');
        }

        const sample: Sample = data.sample;
        // this.selectedSample = sample;
        // console.log(" --- Selected sample:", this.selectedSample);
        if (typeof window !== 'undefined' && etaLoader && sessionId) {
          etaLoader.setStage('loadXML_start', 0.6, undefined, sessionId);
        }
        let author = sample.metadata?.author || {
          en: 'Unknown Author',
          ru: 'Неизвестный автор',
        };

        // load audio;
        const audiopath = sample.audioPath;
        if (audiopath) {
          try {
            const audioStart = performance.now();
            const blob = await this.getAudio(sample);
            console.log(`[PERF] getAudio: ${(performance.now() - audioStart).toFixed(0)}ms`);
            if (blob) {
              audioPlayerModel.loadBlob(blob);
            }
          } catch (audioError) {
            throw audioError; // Re-throw to be caught by outer catch
          }
        }
        if (typeof window !== 'undefined' && etaLoader && sessionId) {
          etaLoader.setStage('loadXML_start', 1, undefined, sessionId);
          etaLoader.setStage('loadXML_done', 0, undefined, sessionId);
        }
        
        if (
          ManagerEntity.player?.eventsRecorder.debugData &&
          Object.keys(ManagerEntity.player.eventsRecorder.debugData.events)
            .length > 0
        ) {
          await apiSwagger.debugEvents.autosaveCreate(
            ManagerEntity.player?.eventsRecorder.debugData as any
          );
          ManagerEntity.player.eventsRecorder.reset();
        }
        
        // start to record new events
        // Check if this is the same sample and prepare gamification context
        const { gamificationStore } = getStores(false, {});
        const isSameSample = this.selectedSample?.identifier === sampleIdentifier;
        const shouldRestoreProgress = isSameSample && gamificationStore.saveProgressEnabled;
        const gamificationContext = {
          shouldRestoreProgress,
          // Only pass lastMeasureIndex if we're actually restoring progress, otherwise start from 0
          lastMeasureIndex: shouldRestoreProgress ? gamificationStore.gameState.lastMeasureIndex : 0,
          sampleId: sampleIdentifier
        };
        // BUG: THE isPrivate prop only hides the sample from search but if I go from URL - the sample does not return error, I can visit the page
        // ManagerEntity is guaranteed to be fully initialized at this point
        // because selectSample is only called after 'managerInited' event fires
        if (hasServerRenderedSvgs) {
          // Use server-rendered SVGs flow (no Verovio required)
          console.log('[SamplesSelectorModel] Using server-rendered SVGs');

          // Get container directly from DOM to ensure we have the current element
          // (renderer._container may be stale after navigation)
          const container = document.getElementById('renderer-sheet');
          if (!container) {
            throw new Error('Renderer container (#renderer-sheet) not found in DOM');
          }

          // Also update the renderer's container reference
          if (ManagerEntity.renderer) {
            ManagerEntity.renderer.setContainer(container);
          }

          // Use ServerRenderedSampleService exposed on window by core
          const loadServerRenderedSample = (window as any).loadServerRenderedSample;
          const fetchCacheData = (window as any).fetchCacheData;
          if (!loadServerRenderedSample) {
            throw new Error('loadServerRenderedSample not available on window. Core may not be loaded.');
          }
          if (!fetchCacheData) {
            throw new Error('fetchCacheData not available on window. Core may not be loaded.');
          }

          // Load SVGs and fetch cache data IN PARALLEL
          const parallelStart = performance.now();
          const [loadResult, cacheData] = await Promise.all([
            loadServerRenderedSample({
              svgUrls: data.svgUrls!,
              noteCoordinatesUrl: data.noteCoordinatesUrl!,
              cacheUrl: data.cacheUrl!
            }, container),
            fetchCacheData(data.cacheUrl!)
          ]);
          console.log(`[PERF] parallel SVG+cache fetch: ${(performance.now() - parallelStart).toFixed(0)}ms`);

          if (!loadResult.success) {
            throw new Error(`Failed to load server-rendered SVGs: ${loadResult.error}`);
          }

          // Initialize timemap from pre-fetched cache data (SVGs already in DOM)
          const setMusicxmlStart = performance.now();
          await ManagerEntity.setMusicxmlFromCache(
            cacheData,
            sample.notesCustomAttributes,
            gamificationContext
          );
          console.log(`[PERF] setMusicxmlFromCache: ${(performance.now() - setMusicxmlStart).toFixed(0)}ms`);
        } else {
          // Legacy flow (requires Verovio - will throw error if called)
          console.warn('[SamplesSelectorModel] No server-rendered SVGs available, using legacy flow');
          await ManagerEntity.setMusicxml(
            encryptedMusicxml,
            false,
            cache,
            sample.notesCustomAttributes,
            true,
            gamificationContext
          );
        }
        
        if (typeof window !== 'undefined' && etaLoader && sessionId) {
          etaLoader.setStage('loadXML_done', 0.5, undefined, sessionId);
        }
        
        this.selectedSample = sample;

        // Update preparingMusicEngine if this is initial load (global context)
        const isInitialLoad = typeof window !== 'undefined' && etaLoader && etaLoader.context === 'global';
        if (isInitialLoad) {
          etaLoader.setStage('preparingMusicEngine', 0.6);
        }
        
        const sampleInfoSuccess = await samplesModel.setSampleInfo({
          author,
          title: sample.metadata?.title || { en: '', ru: '' },
        });
        
        // Only complete preparingMusicEngine if we successfully fetched real author/category data
        if (isInitialLoad) {
          if (sampleInfoSuccess) {
            etaLoader.setStage('preparingMusicEngine', 1);
          } else {
            console.warn('Failed to fetch complete sample info, but continuing...');
            etaLoader.setStage('preparingMusicEngine', 1);
          }
        }
        
        if (typeof window !== 'undefined' && etaLoader && sessionId) {
          etaLoader.setStage('loadXML_done', 0.6, undefined, sessionId);
        }
        if (isAdminPage()) {
          MapperEntity.refreshXmlObject(ManagerEntity.decryptMusicxml(encryptedMusicxml));
        }

        if (ManagerEntity.player && ManagerEntity.player.selectedPracticeMode) {
          ManagerEntity.player.eventsRecorder.startRecording(
            ManagerEntity.player.selectedPracticeMode.id,
            sampleIdentifier
          );
        }
        if (typeof window !== 'undefined' && etaLoader && sessionId) {
          etaLoader.setStage('loadXML_done', 0.75, undefined, sessionId);
        }
        // Check for new version of core from server
        try {
          const response = await fetch('/api/coreUpdated');

          if (response.ok) {
            const serverConfig = await response.json();
            
            // If we don't have a stored version, store the current one
            if (serverConfig.version !== this.coreVersion) {
              console.log(`[SamplesSelector] New core version ${serverConfig.version} detected, reloading...`);
              safeWindow.location.reload();
              return; // Early return since we're reloading
            }
          }
        } catch (error) {
          console.warn('[SamplesSelector] Failed to check for version updates:', error);
        }
        if (typeof window !== 'undefined' && etaLoader && sessionId) {
          etaLoader.setStage('loadXML_done', 0.9, undefined, sessionId);
        }
      } else {
        // Try to get detailed error information from the response
        let errorMessage = 'Failed to load sample';
        try {
          const errorData = await response.json();
          if (errorData.msg) {
            errorMessage = errorData.msg;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch {
          // If JSON parsing fails, use the status text
          errorMessage = response.statusText || `HTTP ${response.status}`;
        }
        if (typeof window !== 'undefined' && etaLoader && sessionId) {
          etaLoader.setStage('loadXML_done', 0.7, undefined, sessionId);
        }
        // Don't show toast here - let the catch block handle it after fallback attempt

        // Throw error with detailed description
        throw new Error(`Sample loading failed: ${errorMessage} (Status: ${response.status})`);
      }
    
      // Clear any previous error state on successful load
      if (typeof window !== 'undefined' && etaLoader && etaLoader.hasError) {
        etaLoader.clearError();
      }

      console.log(`[PERF] ========== selectSample TOTAL: ${(performance.now() - totalStart).toFixed(0)}ms ==========`);
    } else {
      console.error('Ошибка: У сэмпла нет идентификатора');
    }
    samplesDiagnosticPopupModel.stopMessageListening();
    } catch (error: any) {
      // Check if this is a transient initialization error (Assertion failed = renderer/player not ready)
      // Note: Be careful not to catch legitimate errors like 404s - only catch real init errors
      const isInitializationError =
        error?.message === 'Assertion failed' ||
        error?.message?.includes('ManagerEntity') ||
        (error?.stack?.includes('set selection') && !error?.message?.includes('Sample loading failed'));

      if (isInitializationError) {
        samplesDiagnosticPopupModel.stopMessageListening();
        // Don't show error dialog for initialization issues - let React retry on next render
        return;
      }

      // Stop diagnostic popup listening
      samplesDiagnosticPopupModel.stopMessageListening();

      // Log the error
      console.error('Sample loading error:', error);

      // Show error toast only after fallback also failed
      toast({
        title: t('error'),
        description: t('samples.load_failed'),
        type: 'error',
      });

      // If fallback failed or no fallback available, show error UI
      if (typeof window !== 'undefined' && etaLoader) {
       if (etaLoader.context === 'loadXML') {
          // Complete loadXML context stages
          etaLoader.setStage('loadXML_start', 1);
          etaLoader.setStage('loadXML_done', 1);
        }
      }
      // Close the samples selector popover immediately
      this.samplesMenuOpen = false;

      // Clear the selected sample
      this.selectedSample = null;

      // Wait for loader animation to complete before showing error
      const errorMessage = t('samples.load_error_dialog.description');
      setTimeout(() => {
        // Double-check session is still active before showing error
        if (sessionId && !etaLoader.isSessionActive(sessionId)) {
          return;
        }

        // Show error page with action to open samples menu when user clicks button
        etaLoader.showError(errorMessage, () => {
          // This runs when user clicks "Choose Another Piece" button
          this.samplesMenuOpen = true;
        });
      }, 1300); // Wait for the loader to animate to 100% and fade out
    } finally {
      if(typeof window !== 'undefined' && etaLoader && sessionId) {
        etaLoader.setStage('loadXML_done', 1, undefined, sessionId);
      }
    }
  }

  async getAudio(sample: any) {
    const response = await apiSwagger.samples.audioDetail(sample._id);
    if (response.ok) {
      const data = await response.json();

      // Fetch audio from CDN URL
      if (data.audioUrl) {
        const audioResponse = await fetch(data.audioUrl);
        
        // Check if the CDN fetch was successful
        if (!audioResponse.ok) {
          console.error(`Failed to fetch audio from CDN: ${audioResponse.status} ${audioResponse.statusText}`);
          throw new Error(`Audio file not found (${audioResponse.status})`);
        }
        
        const audioBase64 = await audioResponse.text();

        // Decrypt if encrypted
        const audioData = data.encrypted
          ? ManagerEntity.decryptBinary(audioBase64)
          : Buffer.from(audioBase64, 'base64');

        // Convert to Blob
        const blobData = new Blob([audioData], { type: 'audio/mpeg' });
        return blobData;
      }

      return null;
    } else {
      toast({
        title: t('error'),
        description: t('samples.audio_load_failed'),
        type: 'error',
      });
      console.error(
        `Ошибка: Ошибка при получении аудио для сэмпла: ${rightLanguage(
          sample.metadata.title
        )}`
      );
    }
    return null;
  }

  async getMidi(sample: any) {
    const response = await apiSwagger.samples.midiDetail(sample._id);
    if (response.ok) {
      const data = await response.json();

      // Fetch MIDI from CDN URL
      if (data.midiUrl) {
        const midiResponse = await fetch(data.midiUrl);
        const midiBase64 = await midiResponse.text();

        // Decrypt if encrypted
        const midiData = data.encrypted
          ? ManagerEntity.decryptBinary(midiBase64)
          : Buffer.from(midiBase64, 'base64');

        // Convert to ArrayBuffer
        return midiData.buffer;
      }

      return null;
    }
    return null;
  }

  private samplesCache: Sample[] | null = null;
  async selectFavorites(favorites: FavoriteItem[]): Promise<Sample[]> {
    if (favorites.length === 0) return [];

    // refresh only if cache is empty
    if (!this.samplesCache || this.samplesCache.length === 0) {
      await this.refreshSamples();
      this.samplesCache = this.samples; // cache the samples
    }

    const favoriteIdsSet = new Set(favorites.map(fav => fav.id.toString()));

    return this.samplesCache.filter(sample => favoriteIdsSet.has(sample.identifier.toString()));
  }
}

const samplesSelectorModel = new SamplesSelectorModel();
registerGlobalInstance('samplesSelectorModel', samplesSelectorModel);
export default samplesSelectorModel;
